/**
 * Режим Telegram Mini App. Страница tg.html подключает скрипт Telegram и открывается внутри мессенджера;
 * на обычном сайте скрипта нет, и все функции здесь ничего не делают.
 *
 * Документ, как и на сайте, проверяется и исправляется на устройстве. Скачать файл, собранный в браузере,
 * внутри Telegram нельзя, поэтому готовый .docx уходит в чат с ботом через наш сервер (packages/bot):
 * он пересылает файл в Telegram и ничего не сохраняет.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

interface WebApp {
  initData: string;
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  ready(): void;
  expand(): void;
  isVersionAtLeast(version: string): boolean;
  onEvent(event: 'themeChanged', handler: () => void): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  requestWriteAccess?(callback: (granted: boolean) => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

/** Mini App внутри Telegram: есть и скрипт, и подписанные данные запуска. */
export function telegram(): WebApp | undefined {
  const app = typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
  return app?.initData ? app : undefined;
}

export const inTelegram = (): boolean => telegram() !== undefined;

export function initTelegram(): void {
  const app = telegram();
  if (!app) return;
  document.documentElement.classList.add('telegram');
  applyTheme(app);
  app.onEvent('themeChanged', () => applyTheme(app));
  app.ready();
  app.expand();
  // Свайп вниз при прокрутке отчёта не должен закрывать приложение.
  if (app.isVersionAtLeast('7.7')) app.disableVerticalSwipes?.();
}

/** Цвета интерфейса — из темы Telegram, чтобы приложение не выглядело чужим. */
function applyTheme(app: WebApp): void {
  const t = app.themeParams;
  const style = document.documentElement.style;
  const set = (name: string, value: string | undefined): void => {
    if (value) style.setProperty(name, value);
  };
  const bg = t.secondary_bg_color ?? t.bg_color;
  const surface = t.section_bg_color ?? t.bg_color;
  set('--bg', bg);
  set('--surface', surface);
  set('--surface-2', t.bg_color);
  set('--paper', '#ffffff');
  set('--text', t.text_color);
  set('--text-2', t.subtitle_text_color ?? t.hint_color);
  set('--text-3', t.hint_color);
  set('--border', t.section_separator_color);
  set('--accent', t.button_color ?? t.link_color);
  set('--accent-hover', t.button_color ?? t.link_color);
  set('--accent-text', t.button_text_color);
  set('--error', t.destructive_text_color);
  style.colorScheme = app.colorScheme;
  if (bg && app.isVersionAtLeast('6.1')) {
    app.setHeaderColor?.(bg);
    app.setBackgroundColor?.(bg);
  }
}

/** Отправляет готовый файл в чат с ботом. Если бот ещё не может писать пользователю — спрашивает разрешение. */
export async function sendToChat(data: ArrayBuffer, name: string, caption: string): Promise<void> {
  const app = telegram();
  if (!app) throw new Error('Отправка в чат работает только в Telegram');
  const post = (): Promise<Response> =>
    fetch('api/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Telegram-Init-Data': app.initData,
        'X-File-Name': encodeURIComponent(name),
        'X-Caption': encodeURIComponent(caption),
      },
      body: data.slice(0),
    });
  let res = await post();
  if (res.status === 403 && app.requestWriteAccess) {
    const granted = await new Promise<boolean>((done) => app.requestWriteAccess!(done));
    if (!granted) throw new Error('Разрешите боту писать вам — иначе он не сможет прислать файл в чат');
    res = await post();
  }
  if (res.ok) return;
  throw new Error(
    res.status === 413
      ? 'Файл слишком большой для отправки в Telegram'
      : res.status === 429
        ? 'Слишком много файлов за час — попробуйте позже'
        : res.status === 401
          ? 'Сессия Telegram устарела — закройте и снова откройте приложение'
          : 'Не удалось отправить файл в чат — попробуйте ещё раз',
  );
}
